import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { startGoogleLogin } from "@/lib/googleAuth";
import { toast } from "sonner";
import { Mail, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bentornato!");
      nav("/feed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => startGoogleLogin();

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/5807637/pexels-photo-5807637.jpeg?auto=compress&cs=tinysrgb&w=1600)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/45 to-black/15" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="font-display font-black text-2xl uppercase tracking-tighter text-white" data-testid="login-back-home">
            Bicy<span className="text-[#E11D48]">club</span>
          </Link>
          <div>
            <h1 className="font-display font-black text-5xl uppercase tracking-tighter text-white leading-[0.9]">
              Riprendi<br/>la <span className="text-[#E11D48]">strada</span>.
            </h1>
            <p className="text-gray-200 mt-4 max-w-md">Accedi per pubblicare giri, vedere i prossimi appuntamenti e chattare con il tuo gruppo.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6">
          <div>
            <div className="label-tag mb-2">Accedi</div>
            <h2 className="font-display font-black text-4xl uppercase tracking-tighter text-gray-900">Bentornato</h2>
          </div>

          <button
            type="button"
            onClick={googleLogin}
            data-testid="login-google-btn"
            className="w-full bg-white text-black font-bold uppercase tracking-wider text-sm py-3 rounded-sm flex items-center justify-center gap-3 hover:bg-gray-200 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.997 10.997 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continua con Google
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-white/15" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-gray-500">oppure</span>
            <div className="flex-1 h-px bg-white/15" />
          </div>

          <div>
            <label className="label-tag block mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                data-testid="login-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@esempio.it"
                className="w-full bg-white border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm pl-10 pr-4 py-3 text-gray-900"
              />
            </div>
          </div>

          <div>
            <label className="label-tag block mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                data-testid="login-password-input"
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-white border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm pl-10 pr-4 py-3 text-gray-900"
              />
            </div>
          </div>

          <button
            data-testid="login-submit-btn"
            type="submit" disabled={loading}
            className="w-full btn-primary py-3 rounded-sm disabled:opacity-50"
          >
            {loading ? "Accesso..." : "Accedi"}
          </button>

          <p className="text-center text-sm text-gray-500">
            Non hai un account? <Link to="/register" className="text-[#E11D48] font-semibold hover:underline" data-testid="login-go-register">Iscriviti</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
